import { http } from '@noor/api';
import { env } from '@noor/config';
import {
  citySchema,
  gridInitSchema,
  gridMultiplierSchema,
  type City,
  type CityInput,
  type GridBox,
  type GridInit,
  type GridMultiplier,
} from '../model/schema';

/** Search-service admin routes live on the Delivery host, not the main gateway. */
const deliveryHost = { baseURL: env.VITE_DELIVERY_API_URL };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Rows of a `{ list }` envelope, parsed at the boundary. */
function readList<T>(raw: unknown, parse: (row: unknown) => T): T[] {
  const rows = isRecord(raw) && Array.isArray(raw.list) ? raw.list : [];
  return rows.map(parse);
}

function readData(raw: unknown): Record<string, unknown> {
  const data = isRecord(raw) ? raw.data : undefined;
  return isRecord(data) ? data : {};
}

export const cityApi = {
  /** `GET /admin/cities` — unpaginated; the API takes no filters. */
  list: async (): Promise<City[]> =>
    readList(await http.get<unknown>('/api/v1/admin/cities', deliveryHost), (row) =>
      citySchema.parse(row),
    ),

  /** `POST /admin/cities` — `{ data: { id, lat_km_per_degree, ..., city } }`. */
  create: async (input: CityInput): Promise<City> => {
    const data = readData(
      await http.post<unknown>('/api/v1/admin/cities', input, deliveryHost),
    );
    return citySchema.parse(data.city);
  },

  /** `PUT /admin/cities/{id}` — `{ data: { city } }`. */
  update: async (id: string, input: CityInput): Promise<City> => {
    const data = readData(
      await http.put<unknown>(`/api/v1/admin/cities/${id}`, input, deliveryHost),
    );
    return citySchema.parse(data.city);
  },
};

export const gridApi = {
  /** `GET /admin/grid?city_id` — grid sizing: centre and box radius. */
  values: async (cityId: string): Promise<GridInit | null> => {
    const data = readData(
      await http.get<unknown>('/api/v1/admin/grid', {
        ...deliveryHost,
        query: { city_id: cityId },
      }),
    );
    return isRecord(data.grid) ? gridInitSchema.parse(data.grid) : null;
  },

  /** `GET /admin/grid/multipliers?city_id` — one row per grid box. */
  multipliers: async (cityId: string): Promise<GridMultiplier[]> => {
    const raw = await http.get<unknown>('/api/v1/admin/grid/multipliers', {
      ...deliveryHost,
      query: { city_id: cityId },
    });
    // An invalid response must never look like an empty blocked set: that could
    // accidentally activate every box during the next replacement write.
    if (!isRecord(raw) || !Array.isArray(raw.list)) {
      throw new Error('Invalid grid multipliers response');
    }
    return raw.list.map((row) => gridMultiplierSchema.parse(row));
  },

  /** `POST /admin/grid/deactivate` — replaces the city’s complete blocked set; [] activates all. */
  deactivate: async (cityId: string, boxes: GridBox[]): Promise<void> => {
    await http.post<unknown>(
      '/api/v1/admin/grid/deactivate',
      { city_id: cityId, boxes },
      deliveryHost,
    );
  },
};
